import React, { useState, useEffect, useMemo, useCallback } from "react";
import Select from "react-select";
import {
  Card,
  CardBody,
  Col,
  Container,
  Form,
  Input,
  Label,
  Row,
  Button,
  Table,
} from "reactstrap";
import { getDailySummary, getDailySummaryDetails, exportDailySummaryToExcel, exportDailySummaryDetailsToExcel } from "../../components/Api/GmsDailySummary";
import { getBranches } from "../../components/Api/GmsBranch";
import { Filter, Download, ChevronDown, ChevronRight } from "lucide-react";
import { ClipLoader } from "react-spinners";
import Swal from "sweetalert2";
import {
  hasPagePermissionWithAdmin,
  isUserAdmin
} from "../../components/Permission/PermissionService";
import Breadcrumb from "../../components/Common/Breadcrumb";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";

// Custom styles for Select components (from PayrollProcessingPage)
const selectStyles = {
  control: (provided, state) => ({
    ...provided,
    minHeight: '38px',
    borderColor: state.isFocused ? '#556ee6' : '#ced4da',
    boxShadow: state.isFocused ? '0 0 0 0.2rem rgba(85, 110, 230, 0.25)' : 'none',
  }),
  menu: (provided) => ({
    ...provided,
    zIndex: 9999,
    maxHeight: 200,
  }),
  menuList: (provided) => ({
    ...provided,
    maxHeight: 180,
  }),
  option: (provided, state) => ({
    ...provided,
    backgroundColor: state.isSelected ? '#556ee6' : state.isFocused ? '#f8f9fa' : 'white',
    color: state.isSelected ? 'white' : '#495057',
    '&:hover': {
      backgroundColor: state.isSelected ? '#556ee6' : '#e9ecef',
    },
  }),
};

// Get user session
const getUserSession = () => {
  const authUser = JSON.parse(localStorage.getItem("authUser") || "{}");
  const user = JSON.parse(localStorage.getItem("user") || "{}");
  return {
    userId: authUser.userId || 1,
    userName: authUser.userName || "System",
    branchId: user.user_Branch_Id || null,
  };
};

export default function DailySummaryReportPage() {
  const [summaryData, setSummaryData] = useState([]);
  const [detailsData, setDetailsData] = useState({}); // { date: [invoices] }
  const [expandedRows, setExpandedRows] = useState(new Set()); // Track which rows are expanded
  const [loadingDetails, setLoadingDetails] = useState({}); // Track loading per date
  const [page, setPage] = useState(1);
  const [pageSize] = useState(10); // Fixed, not user-selectable
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  const [branches, setBranches] = useState([]);
  const [selectedBranch, setSelectedBranch] = useState(null);
  const [showBranchDropdown, setShowBranchDropdown] = useState(true);

  // Date filter state
  const [fromDate, setFromDate] = useState(new Date());
  const [toDate, setToDate] = useState(new Date());

  // Check permissions
  const permissions = JSON.parse(localStorage.getItem('permissions') || '[]');
  const isAdmin = isUserAdmin();
  const hasPermission = hasPagePermissionWithAdmin(permissions, '/GmsDailySummary');

  // Get user session on mount
  const session = useMemo(() => getUserSession(), []);
  const userBranchId = session.branchId;

  // Define functions first before using them in useEffect
  const loadBranches = useCallback(async () => {
    try {
      const data = await getBranches({});
      setBranches(data || []);
    } catch (error) {
      console.error("Error loading branches:", error);
      setBranches([]);
    }
  }, []);

  // Format dates properly to avoid timezone issues (same as BookingPage)
  const formatDateForAPI = (date) => {
    if (!date) return null;
    // Create date in local timezone and format as YYYY-MM-DD
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const loadSummaryData = useCallback(async () => {
    try {
      setLoading(true);

      // Format dates as YYYY-MM-DD strings to avoid timezone issues
      const formattedFromDate = formatDateForAPI(fromDate);
      const formattedToDate = formatDateForAPI(toDate);

      const filter = {
        fromDate: formattedFromDate,
        toDate: formattedToDate,
        branch_Id: selectedBranch?.value || null,
        business_Id: null
      };

      console.log("📊 Loading summary data with filter:", filter);

      const response = await getDailySummary(filter);

      console.log("📊 Summary response:", response);

      // Handle both camelCase and PascalCase response structures
      const summaries = response?.summaries || response?.Summaries || [];

      console.log("📊 Parsed summaries:", summaries);

      if (summaries && Array.isArray(summaries)) {
        setSummaryData(summaries);
        setTotalCount(summaries.length);
      } else {
        setSummaryData([]);
        setTotalCount(0);
      }
    } catch (error) {
      console.error("❌ Error loading summary data:", error);
      Swal.fire("Error", "Failed to load daily summary report", "error");
      setSummaryData([]);
      setTotalCount(0);
    } finally {
      setLoading(false);
    }
  }, [fromDate, toDate, selectedBranch]);

  useEffect(() => {
    if (hasPermission) {
      // Check if user has branch - if yes, auto-filter and hide dropdown
      if (userBranchId) {
        setShowBranchDropdown(false);
        // Load branches to get the label, then set selected branch
        loadBranches();
      } else {
        setShowBranchDropdown(true);
        loadBranches();
      }
    } else {
      window.location.href = '/Dashboard';
    }
  }, [hasPermission, userBranchId, loadBranches]);

  // Set selected branch when branches are loaded and user has branchId
  useEffect(() => {
    if (userBranchId && branches.length > 0 && !selectedBranch) {
      const branch = branches.find(b => b.branch_Id === userBranchId);
      if (branch) {
        setSelectedBranch({
          value: userBranchId,
          label: `${branch.branch_NameEn} - ${branch.branch_NameAr}`
        });
      }
    }
  }, [branches, userBranchId, selectedBranch]);

  // Load summary data when branch is set (for users with branchId) or on initial load
  useEffect(() => {
    if (hasPermission) {
      // If user has branchId, wait for branch to be set; otherwise load immediately
      if (userBranchId) {
        if (selectedBranch) {
          loadSummaryData();
        }
      } else if (!userBranchId) {
        // Only load if user doesn't have branchId (to avoid double loading)
        loadSummaryData();
      }
    }
  }, [hasPermission, selectedBranch, userBranchId, loadSummaryData]);

  const loadDetailsForDate = async (date) => {
    // Handle date - could be Date object or string
    const dateObj = date instanceof Date ? date : new Date(date);
    // Use formatDateForAPI to get YYYY-MM-DD string for the key
    const dateKey = formatDateForAPI(dateObj);

    console.log("🔍 Loading details for date:", {
      originalDate: date,
      dateObj: dateObj,
      dateKey: dateKey,
      formattedForAPI: formatDateForAPI(dateObj)
    });

    // If already loaded, don't reload
    if (detailsData[dateKey]) {
      console.log("✅ Details already loaded for", dateKey);
      return;
    }

    try {
      setLoadingDetails(prev => ({ ...prev, [dateKey]: true }));

      // Format date as YYYY-MM-DD string to avoid timezone issues
      const formattedDate = formatDateForAPI(dateObj);

      const request = {
        date: formattedDate,
        branch_Id: selectedBranch?.value || null,
        business_Id: null
      };

      console.log("📤 Sending details request:", request);

      const response = await getDailySummaryDetails(request);

      console.log("📥 Details response:", response);

      // Handle both camelCase and PascalCase response structures
      const details = response?.details || response?.Details || [];

      console.log("✅ Parsed details count:", details.length, details);

      setDetailsData(prev => ({
        ...prev,
        [dateKey]: Array.isArray(details) ? details : []
      }));
    } catch (error) {
      console.error("❌ Error loading details for date:", error);
      console.error("❌ Error response:", error.response?.data);
      Swal.fire("Error", `Failed to load invoice details for ${formatDate(dateObj)}`, "error");
      setDetailsData(prev => ({
        ...prev,
        [dateKey]: []
      }));
    } finally {
      setLoadingDetails(prev => ({ ...prev, [dateKey]: false }));
    }
  };

  const handleToggleRow = (date) => {
    // Handle date - could be Date object or string
    const dateObj = date instanceof Date ? date : new Date(date);
    // Use formatDateForAPI to get consistent YYYY-MM-DD string
    const dateKey = formatDateForAPI(dateObj);
    const newExpandedRows = new Set(expandedRows);

    console.log("🔄 Toggling row for date:", {
      originalDate: date,
      dateObj: dateObj,
      dateKey: dateKey
    });

    if (newExpandedRows.has(dateKey)) {
      newExpandedRows.delete(dateKey);
    } else {
      newExpandedRows.add(dateKey);
      // Load details if not already loaded
      if (!detailsData[dateKey]) {
        loadDetailsForDate(dateObj);
      }
    }

    setExpandedRows(newExpandedRows);
  };

  const handleApplyFilter = () => {
    setPage(1);
    setExpandedRows(new Set());
    setDetailsData({});
    loadSummaryData();
  };

  const handleClearFilter = () => {
    const today = new Date();
    setFromDate(today);
    setToDate(today);
    if (!userBranchId) {
      setSelectedBranch(null);
    }
    setPage(1);
    setExpandedRows(new Set());
    setDetailsData({});
    loadSummaryData();
  };

  const handlePageChange = (newPage) => {
    setPage(newPage);
  };

  const handleExportSummary = async () => {
    try {
      setLoading(true);

      // Format dates as YYYY-MM-DD strings to avoid timezone issues
      const formattedFromDate = formatDateForAPI(fromDate);
      const formattedToDate = formatDateForAPI(toDate);

      const filter = {
        fromDate: formattedFromDate,
        toDate: formattedToDate,
        branch_Id: selectedBranch?.value || null,
        business_Id: null
      };

      console.log("📤 Exporting summary with filter:", filter);

      await exportDailySummaryToExcel(filter);
      Swal.fire("Success", "Daily summary report exported successfully", "success");
    } catch (error) {
      console.error("❌ Error exporting summary:", error);
      Swal.fire("Error", "Failed to export daily summary report", "error");
    } finally {
      setLoading(false);
    }
  };

  const handleExportDetails = async (date) => {
    try {
      setLoading(true);

      // Handle date - could be Date object or string
      const dateObj = date instanceof Date ? date : new Date(date);
      // Format date as YYYY-MM-DD string to avoid timezone issues
      const formattedDate = formatDateForAPI(dateObj);

      const request = {
        date: formattedDate,
        branch_Id: selectedBranch?.value || null,
        business_Id: null
      };

      console.log("📤 Exporting details with request:", request);

      await exportDailySummaryDetailsToExcel(request);
      Swal.fire("Success", "Invoice details exported successfully", "success");
    } catch (error) {
      console.error("❌ Error exporting details:", error);
      Swal.fire("Error", "Failed to export invoice details", "error");
    } finally {
      setLoading(false);
    }
  };

  // Get paginated data for display
  const getPaginatedData = () => {
    const startIndex = (page - 1) * pageSize;
    const endIndex = startIndex + pageSize;
    return summaryData.slice(startIndex, endIndex);
  };

  // Calculate grand totals
  const grandTotals = useMemo(() => {
    return summaryData.reduce((acc, row) => ({
      salesAmount: acc.salesAmount + (row.salesAmount || 0),
      salesReturnAmount: acc.salesReturnAmount + (row.salesReturnAmount || 0),
      totalSalesCredit: acc.totalSalesCredit + (row.totalSalesCredit || 0),
      totalSalesReturnCredit: acc.totalSalesReturnCredit + (row.totalSalesReturnCredit || 0),
      serviceAmountCash: acc.serviceAmountCash + (row.serviceAmountCash || 0),
      serviceAmountCredit: acc.serviceAmountCredit + (row.serviceAmountCredit || 0),
      totalCash: acc.totalCash + (row.totalCash || 0),
      cash: acc.cash + (row.cash || 0),
      creditCard: acc.creditCard + (row.creditCard || 0),
      bankTransfer: acc.bankTransfer + (row.bankTransfer || 0),
      tabee: acc.tabee + (row.tabee || 0),
      online: acc.online + (row.online || 0),
      totalAmount: acc.totalAmount + (row.totalAmount || 0),
      invoiceCount: acc.invoiceCount + (row.invoiceCount || 0),
    }), {
      salesAmount: 0,
      salesReturnAmount: 0,
      totalSalesCredit: 0,
      totalSalesReturnCredit: 0,
      serviceAmountCash: 0,
      serviceAmountCredit: 0,
      totalCash: 0,
      cash: 0,
      creditCard: 0,
      bankTransfer: 0,
      tabee: 0,
      online: 0,
      totalAmount: 0,
      invoiceCount: 0,
    });
  }, [summaryData]);

  // Format currency
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(amount || 0);
  };

  // Format date
  const formatDate = (dateString) => {
    try {
      const date = new Date(dateString);
      const day = date.getDate().toString().padStart(2, '0');
      const month = (date.getMonth() + 1).toString().padStart(2, '0');
      const year = date.getFullYear();
      return `${day}/${month}/${year}`;
    } catch (error) {
      return "N/A";
    }
  };

  // Branch options
  const branchOptions = useMemo(() => {
    return branches.map(branch => ({
      value: branch.branch_Id,
      label: `${branch.branch_NameEn} - ${branch.branch_NameAr}`
    }));
  }, [branches]);

  if (!hasPermission) {
    return null;
  }

  const paginatedData = getPaginatedData();

  return (
    <div className="page-content">
      <Container fluid>
        <Breadcrumb title="Daily Summary Report" breadcrumbItem="Reports" />

        <Row>
          <Col lg="12">
            <Card>
              <CardBody>
                <div className="d-flex justify-content-between align-items-center mb-3">
                  <h4 className="card-title mb-0">Daily Summary Report</h4>
                  <div>
                    <Button
                      color="primary"
                      className="me-2"
                      onClick={() => setShowFilters(!showFilters)}
                    >
                      <Filter className="me-1" size={16} />
                      {showFilters ? 'Hide Filters' : 'Show Filters'}
                    </Button>
                    <Button
                      color="success"
                      onClick={handleExportSummary}
                      disabled={loading}
                    >
                      <Download className="me-1" size={16} />
                      Export Summary
                    </Button>
                  </div>
                </div>

                {/* Filters Section */}
                {showFilters && (
                  <Card className="mb-4">
                    <CardBody>
                      <Row>
                        <Col md="3">
                          <Label>From Date</Label>
                          <DatePicker
                            selected={fromDate}
                            onChange={(date) => setFromDate(date)}
                            dateFormat="dd/MM/yyyy"
                            className="form-control"
                            maxDate={toDate}
                          />
                        </Col>
                        <Col md="3">
                          <Label>To Date</Label>
                          <DatePicker
                            selected={toDate}
                            onChange={(date) => setToDate(date)}
                            dateFormat="dd/MM/yyyy"
                            className="form-control"
                            minDate={fromDate}
                          />
                        </Col>
                        {showBranchDropdown && (
                          <Col md="3">
                            <Label>Branch</Label>
                            <Select
                              options={branchOptions}
                              value={selectedBranch}
                              onChange={(option) => setSelectedBranch(option)}
                              isClearable
                              isSearchable
                              placeholder="Select Branch"
                              styles={selectStyles}
                              maxMenuHeight={200}
                              menuPlacement="auto"
                            />
                          </Col>
                        )}
                        <Col md={showBranchDropdown ? "3" : "6"}>
                          <div className="d-flex gap-2 mt-4">
                            <Button color="primary" onClick={handleApplyFilter}>
                              Apply Filter
                            </Button>
                            <Button color="secondary" onClick={handleClearFilter}>
                              Clear Filter
                            </Button>
                          </div>
                        </Col>
                      </Row>
                    </CardBody>
                  </Card>
                )}

                {/* Data Table */}
                <div className="table-responsive">
                  {loading ? (
                    <div className="text-center py-4">
                      <ClipLoader color="#556ee6" loading={loading} size={35} />
                      <p className="mt-2">Loading daily summary report...</p>
                    </div>
                  ) : (
                    <>
                      <Table className="table table-striped table-bordered">
                        <thead>
                          <tr>
                            <th style={{ width: '40px' }}></th>
                            <th>Date</th>
                            <th>Sales Amount</th>
                            <th>Sales Return Amount</th>
                            <th>Total Sales (Credit)</th>
                            <th>Total Sales Return (Credit)</th>
                            <th>Service Amount (Cash)</th>
                            <th>Service Amount (Credit)</th>
                            <th>Total Cash</th>
                            <th>Cash</th>
                            <th>Credit Card</th>
                            <th>Bank Transfer</th>
                            <th>Tabee</th>
                            <th>Online</th>
                            <th>Total Amount</th>
                            <th>Invoice Count</th>
                          </tr>
                        </thead>
                        <tbody>
                          {paginatedData.length > 0 ? (
                            paginatedData.map((row, index) => {
                              // Handle date from API - could be string or Date object
                              const rowDate = row.date instanceof Date ? row.date : new Date(row.date);
                              const dateKey = formatDateForAPI(rowDate);
                              const isExpanded = expandedRows.has(dateKey);
                              const dateDetails = detailsData[dateKey] || [];
                              const isLoadingDetails = loadingDetails[dateKey];

                              return (
                                <React.Fragment key={dateKey}>
                                  <tr>
                                    <td>
                                      <Button
                                        color="link"
                                        size="sm"
                                        onClick={() => handleToggleRow(rowDate)}
                                        className="p-0"
                                      >
                                        {isExpanded ? (
                                          <ChevronDown size={16} />
                                        ) : (
                                          <ChevronRight size={16} />
                                        )}
                                      </Button>
                                    </td>
                                    <td>{formatDate(rowDate)}</td>
                                    <td>{formatCurrency(row.salesAmount)}</td>
                                    <td style={{ color: row.salesReturnAmount > 0 ? '#dc3545' : 'inherit' }}>
                                      {formatCurrency(row.salesReturnAmount)}
                                    </td>
                                    <td>{formatCurrency(row.totalSalesCredit)}</td>
                                    <td style={{ color: row.totalSalesReturnCredit > 0 ? '#dc3545' : 'inherit' }}>
                                      {formatCurrency(row.totalSalesReturnCredit)}
                                    </td>
                                    <td>{formatCurrency(row.serviceAmountCash)}</td>
                                    <td>{formatCurrency(row.serviceAmountCredit)}</td>
                                    <td>{formatCurrency(row.totalCash)}</td>
                                    <td>{formatCurrency(row.cash || 0)}</td>
                                    <td>{formatCurrency(row.creditCard || 0)}</td>
                                    <td>{formatCurrency(row.bankTransfer || 0)}</td>
                                    <td>{formatCurrency(row.tabee || 0)}</td>
                                    <td>{formatCurrency(row.online || 0)}</td>
                                    <td><strong>{formatCurrency(row.totalAmount)}</strong></td>
                                    <td>{row.invoiceCount}</td>
                                  </tr>
                                  {isExpanded && (
                                    <tr>
                                      <td colSpan="16" className="p-0">
                                        <div className="p-3" style={{ backgroundColor: '#f8f9fa' }}>
                                          {isLoadingDetails ? (
                                            <div className="text-center py-3">
                                              <ClipLoader color="#556ee6" size={20} />
                                              <p className="mt-2 mb-0">Loading invoice details...</p>
                                            </div>
                                          ) : dateDetails.length > 0 ? (
                                            <div>
                                              <div className="d-flex justify-content-between align-items-center mb-3">
                                                <h6 className="mb-0">Invoice Details for {formatDate(rowDate)}</h6>
                                                <Button
                                                  color="info"
                                                  size="sm"
                                                  onClick={() => handleExportDetails(rowDate)}
                                                  disabled={loading}
                                                >
                                                  <Download size={14} className="me-1" />
                                                  Export Details
                                                </Button>
                                              </div>
                                              <Table size="sm" className="mb-0">
                                                <thead>
                                                  <tr>
                                                    <th>Invoice No</th>
                                                    <th>Customer</th>
                                                    <th>Invoice Type</th>
                                                    <th>Payment Method</th>
                                                    <th>Net Amount</th>
                                                    <th>Cash</th>
                                                    <th>Credit Card</th>
                                                    <th>Bank Transfer</th>
                                                    <th>Tabee</th>
                                                    <th>Online</th>
                                                    <th>Credit</th>
                                                    <th>Is Return</th>
                                                  </tr>
                                                </thead>
                                                <tbody>
                                                  {dateDetails.map((detail, idx) => (
                                                    <tr key={idx}>
                                                      <td>{detail.invoice_No}</td>
                                                      <td>{detail.customerName || 'N/A'}</td>
                                                      <td>
                                                        <span className={`badge ${detail.invoice_Type === 'ca' ? 'bg-success' : 'bg-warning'}`}>
                                                          {detail.invoice_Type === 'ca' ? 'Cash' : 'Credit'}
                                                        </span>
                                                      </td>
                                                      <td>
                                                        {detail.paymentMethod === 'ca' ? 'Cash' :
                                                          detail.paymentMethod === 'cc' ? 'Credit Card' :
                                                            detail.paymentMethod === 'ba' ? 'Bank Transfer' :
                                                              detail.paymentMethod === 'ta' ? 'Tabee' :
                                                                detail.paymentMethod === 'on' ? 'Online' : 'N/A'}
                                                      </td>
                                                      <td style={{ color: detail.isReturn ? '#dc3545' : 'inherit' }}>
                                                        {formatCurrency(detail.invoice_NetAmount)}
                                                      </td>
                                                      <td style={{ color: detail.isReturn && (detail.cashAmount || 0) > 0 ? '#dc3545' : 'inherit' }}>
                                                        {formatCurrency(detail.cashAmount || 0)}
                                                      </td>
                                                      <td style={{ color: detail.isReturn && (detail.creditCardAmount || 0) > 0 ? '#dc3545' : 'inherit' }}>
                                                        {formatCurrency(detail.creditCardAmount || 0)}
                                                      </td>
                                                      <td style={{ color: detail.isReturn && (detail.bankTransferAmount || 0) > 0 ? '#dc3545' : 'inherit' }}>
                                                        {formatCurrency(detail.bankTransferAmount || 0)}
                                                      </td>
                                                      <td style={{ color: detail.isReturn && (detail.tabeeAmount || 0) > 0 ? '#dc3545' : 'inherit' }}>
                                                        {formatCurrency(detail.tabeeAmount || 0)}
                                                      </td>
                                                      <td style={{ color: detail.isReturn && (detail.onlineAmount || 0) > 0 ? '#dc3545' : 'inherit' }}>
                                                        {formatCurrency(detail.onlineAmount || 0)}
                                                      </td>
                                                      <td style={{ color: detail.isReturn && (detail.creditAmount || 0) > 0 ? '#dc3545' : 'inherit' }}>
                                                        {formatCurrency(detail.creditAmount || 0)}
                                                      </td>
                                                      <td>
                                                        <span className={`badge ${detail.isReturn ? 'bg-danger' : 'bg-success'}`}>
                                                          {detail.isReturn ? 'Yes' : 'No'}
                                                        </span>
                                                      </td>
                                                    </tr>
                                                  ))}
                                                </tbody>
                                              </Table>
                                            </div>
                                          ) : (
                                            <div className="text-center py-3">
                                              <p className="mb-0 text-muted">No invoice details found for this date</p>
                                            </div>
                                          )}
                                        </div>
                                      </td>
                                    </tr>
                                  )}
                                </React.Fragment>
                              );
                            })
                          ) : (
                            <tr>
                              <td colSpan="16" className="text-center">No data found</td>
                            </tr>
                          )}
                        </tbody>
                        {summaryData.length > 0 && (
                          <tfoot>
                            <tr style={{ backgroundColor: '#f8f9fa', fontWeight: 'bold' }}>
                              <td colSpan="2"><strong>Grand Total</strong></td>
                              <td>{formatCurrency(grandTotals.salesAmount)}</td>
                              <td style={{ color: grandTotals.salesReturnAmount > 0 ? '#dc3545' : 'inherit' }}>
                                {formatCurrency(grandTotals.salesReturnAmount)}
                              </td>
                              <td>{formatCurrency(grandTotals.totalSalesCredit)}</td>
                              <td style={{ color: grandTotals.totalSalesReturnCredit > 0 ? '#dc3545' : 'inherit' }}>
                                {formatCurrency(grandTotals.totalSalesReturnCredit)}
                              </td>
                              <td>{formatCurrency(grandTotals.serviceAmountCash)}</td>
                              <td>{formatCurrency(grandTotals.serviceAmountCredit)}</td>
                              <td>{formatCurrency(grandTotals.totalCash)}</td>
                              <td>{formatCurrency(grandTotals.cash)}</td>
                              <td>{formatCurrency(grandTotals.creditCard)}</td>
                              <td>{formatCurrency(grandTotals.bankTransfer)}</td>
                              <td>{formatCurrency(grandTotals.tabee)}</td>
                              <td>{formatCurrency(grandTotals.online)}</td>
                              <td><strong>{formatCurrency(grandTotals.totalAmount)}</strong></td>
                              <td>{grandTotals.invoiceCount}</td>
                            </tr>
                          </tfoot>
                        )}
                      </Table>

                      {/* Pagination */}
                      <div className="d-flex justify-content-between align-items-center mt-4 p-3"
                        style={{ backgroundColor: '#f8f9fa', borderRadius: '0.375rem', border: '1px solid #dee2e6' }}>
                        <div className="text-muted" style={{ fontSize: '14px', fontWeight: '500' }}>
                          <i className="mdi mdi-information-outline me-2" style={{ color: '#556ee6' }}></i>
                          Showing <span style={{ color: '#556ee6', fontWeight: '600' }}>{totalCount > 0 ? (page - 1) * pageSize + 1 : 0}</span> to <span style={{ color: '#556ee6', fontWeight: '600' }}>{Math.min(page * pageSize, totalCount)}</span> of <span style={{ color: '#556ee6', fontWeight: '600' }}>{totalCount}</span> entries
                        </div>
                        {totalCount > pageSize && (
                          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <Button
                              color="light"
                              size="sm"
                              disabled={page === 1}
                              onClick={() => handlePageChange(page - 1)}
                              style={{
                                border: '1px solid #dee2e6',
                                borderRadius: '0.375rem',
                                minWidth: '32px',
                                height: '32px',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                backgroundColor: page === 1 ? '#f8f9fa' : '#fff',
                                color: page === 1 ? '#6c757d' : '#495057'
                              }}
                            >
                              <i className="mdi mdi-chevron-left"></i>
                            </Button>

                            {[...Array(Math.ceil(totalCount / pageSize))].map((_, index) => {
                              const pageNum = index + 1;
                              const isActive = page === pageNum;

                              return (
                                <Button
                                  key={pageNum}
                                  color={isActive ? "primary" : "light"}
                                  size="sm"
                                  onClick={() => handlePageChange(pageNum)}
                                  style={{
                                    border: '1px solid #dee2e6',
                                    borderRadius: '0.375rem',
                                    minWidth: '32px',
                                    height: '32px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    backgroundColor: isActive ? '#556ee6' : '#fff',
                                    borderColor: isActive ? '#556ee6' : '#dee2e6',
                                    color: isActive ? '#fff' : '#495057',
                                    fontWeight: isActive ? '600' : '400',
                                    boxShadow: isActive ? '0 2px 4px rgba(85,110,230,0.25)' : 'none'
                                  }}
                                >
                                  {pageNum}
                                </Button>
                              );
                            })}

                            <Button
                              color="light"
                              size="sm"
                              disabled={page === Math.ceil(totalCount / pageSize)}
                              onClick={() => handlePageChange(page + 1)}
                              style={{
                                border: '1px solid #dee2e6',
                                borderRadius: '0.375rem',
                                minWidth: '32px',
                                height: '32px',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                backgroundColor: page === Math.ceil(totalCount / pageSize) ? '#f8f9fa' : '#fff',
                                color: page === Math.ceil(totalCount / pageSize) ? '#6c757d' : '#495057'
                              }}
                            >
                              <i className="mdi mdi-chevron-right"></i>
                            </Button>
                          </div>
                        )}
                      </div>
                    </>
                  )}
                </div>
              </CardBody>
            </Card>
          </Col>
        </Row>
      </Container>
    </div>
  );
}

